import * as _ from "lodash";
import React, { ComponentProps, FC } from "react";
import Markdown from "react-markdown";
import ReactJson from "react-json-view";
import { useDeepCompareCallback, useDeepCompareMemo } from "use-deep-compare";

import {
  ActionKey,
  Analytics,
  BrowserOutlined,
  Button,
  Col,
  Icon,
  InfoCircleOutlined,
  Row,
  Tooltip
} from "@ctra/components";

import { GAEvent } from "@ctra/analytics";
import { isDebugMode } from "@ctra/utils";
import { Enterprise, useTranslation } from "@ctra/i18n";
import { DataDescriptorEntity, MetricEntity } from "@ctra/api";

import { useFarm } from "@farms";
import { useChartAPI, useChartFilters } from "@chart";
import { useDataDictionary, useUserPreferences } from "@base";
import { GACategories } from "@analytics";

import { MetricPicker } from "../MetricPicker";
import { Filter } from "../Filter";

import styles from "./ChartSelector.module.less";

const {
  V3: { Dropdown }
} = Analytics;

const {
  analytics: { config },
  chart: { description }
} = Enterprise;

export interface BaseIntf {
  /**
   * @note this can be a metric id or a family (which contains multiple metrics)
   */
  metricID: MetricEntity["id"];
  /**
   * @note data descriptor id
   */
  variantID: DataDescriptorEntity["id"];
}

interface ChartSelectorProps extends BaseIntf, Omit<ComponentProps<typeof Row>, "onChange"> {
  /**
   * Disable the selector
   */
  disabled?: boolean;
  /**
   * Blacklist of metrics to be excluded from the options
   */
  blacklist?: Array<BaseIntf>;
  /**
   * Handle change
   * @param {NonNullable<string | undefined>} metric
   * @param {DataDescriptorEntity["id"]} variant
   */
  onChange: (metric: MetricEntity["id"], variant: DataDescriptorEntity["id"]) => void;
  /**
   * Tell whether the option is disabled
   * @param {MetricEntity} metric
   * @returns {boolean}
   */
  isOptionDisabled?: (entity: MetricEntity | DataDescriptorEntity) => boolean;
}

/**
 * Chart selector consisting of metric/sourc/variant combination
 * @param {boolean | undefined} disabled
 * @param {string} metricID
 * @param {string} variantID
 * @param {Array<BaseIntf> | undefined} blacklist
 * @param {(metric: MetricEntity["id"], variant: DataDescriptorEntity["id"]) => void} onChange
 * @param {((metric: MetricEntity) => boolean) | undefined} isOptionDisabled
 * @param {Omit<React.PropsWithChildren<ChartSelectorProps>, "metricID" | "onChange" | "blacklist" | "disabled" | "variantID" | "isOptionDisabled">} rest
 * @returns {JSX.Element}
 * @constructor
 */
export const ChartSelector: FC<ChartSelectorProps> = ({
  disabled,
  metricID,
  variantID,
  blacklist,
  onChange,
  isOptionDisabled,
  ...rest
}) => {
  const { t } = useTranslation();
  const { farm, site } = useFarm();

  const {
    api: { getSeries, getMetadata }
  } = useChartFilters();

  const {
    preferences: {
      sandbox: { isEnabled: isSandboxEnabled }
    }
  } = useUserPreferences();

  const {
    dataDescriptors,
    metrics,
    groups: { families }
  } = useDataDictionary();

  const { charts } = useChartAPI();

  /**
   * Try to find the chart entity to show the info tip
   * @type {{entity: ChartEntity, data: Optional<ChartData["data"]>, meta: Optional<ChartData["meta"]>} | undefined}
   */
  const chart = _.find(charts, ({ entity: { id } }) => {
    const [variant] = _.split(id, "-");

    return variant === variantID;
  });

  /**
   * Grab the metric entity using the metric id
   * @type {MetricEntity}
   */
  const metricEntity: MetricEntity = metrics[metricID];

  if (!metricEntity) {
    throw new Error(`Metric entity not found for metric: ${metricID}`);
  }

  /**
   * Get the metrics in a family
   * @type {Dictionary<unknown>}
   */
  const familyMembers = metricEntity.family ? _.keyBy(_.get(families, [metricEntity.family], []), "id") : {};

  /**
   * Find the variant ids using the metric id
   */
  const variants = metricEntity.variants;

  /**
   * Pick the variants which are supported by the current farm
   * @type {DataDescriptorEntity["id"][]}
   */
  const supportedVariants = useDeepCompareMemo(
    () =>
      farm?.id && site?.id
        ? _.filter(variants, (variant) => {
            const variantEntity = _.get(dataDescriptors, [variant]);

            return (
              _.includes(variantEntity.supportedFarms, farm.id) &&
              variantEntity.dataProperties.type === "site"
            );
          })
        : farm?.id
        ? _.filter(variants, (variant) =>
            _.includes(_.get(dataDescriptors, [variant, "supportedFarms"]), farm.id)
          )
        : variants,
    [variants, farm?.id]
  );

  if (_.isEmpty(supportedVariants)) {
    throw new Error("No supported variants found for the selected metric.");
  }

  /**
   * When the metric changes, find the variant which matches the same type so we don't need to reset all the filters
   * @type {(metric: NonNullable<string | undefined>) => never}
   */
  const trackVariant = useDeepCompareCallback(
    (metric: MetricEntity["id"]) => {
      /**
       * Variants which are excluded
       * @type {string[]}
       */
      const excluded = _.compact(_.map(blacklist, "variantID"));

      /**
       * Get the list of variants from family or the metric
       * @type {string[]}
       */
      const variants = _.get(metrics, [metric, "variants"]);

      /**
       * Remove the excluded variants from the list
       * @type {string[]}
       */
      const filteredVariants = _.without(variants, ...excluded);

      /**
       * Make a map e.g. farm/pen/lactation => variantID.
       * @type {{}}
       */
      const variantsByType = _.reduce(
        filteredVariants,
        (acc, variantID) => {
          const type = _.get(dataDescriptors, [variantID, "dataProperties", "type"]);

          if (_.get(acc, [type])) {
            throw new Error(`Duplicate variant type ${type} for ${variantID}. Revert to using a _.map!`);
          }

          return {
            ...acc,
            [type]: variantID
          };
        },
        {}
      );

      /**
       * Variant type of the current
       * @note this may be a type of which variant is filtered out
       */
      const currentType = _.get(dataDescriptors, [variantID, "dataProperties", "type"]);

      /**
       * Grab either the variant of the same type or flip to the first one available
       * @type {Exclude<{[p: number]: string}[keyof {[p: number]: string}], undefined> | string | undefined}
       */
      return _.get(variantsByType, currentType, _.first(_.values(variantsByType)));
    },
    [metrics, families, dataDescriptors, variantID, blacklist]
  );

  /**
   * Order the metrics by family index
   * @type {() => {value: unknown, label: string}[]}
   */
  const getOrderedMetrics = useDeepCompareCallback(() => {
    const byFamilyIndex = _.orderBy(familyMembers, "familyIndex", "asc");
    const excludedMetrics = _.compact(_.map(blacklist, "metricID"));
    const excludedVariants = _.compact(_.map(blacklist, "variantID"));

    /**
     * Use only the relevant families. "All farms" view can only show variants with "farm" type.
     * @type {MetricEntity[]}
     */
    const relevantFamilies: MetricEntity[] = _.filter(byFamilyIndex, ({ variants }) => {
      return (
        !!farm ||
        _.some(variants, (variantID) => {
          const variantType = _.get(dataDescriptors, [variantID, "dataProperties", "type"]);

          return _.isEqual(variantType, "farm");
        })
      );
    });

    /**
     * Exclude any metric which matches the blacklist AND has only one variant.
     * Metrics with more than one variant may stay as those options will be filtered out in the
     * next dropdown.
     * @type {MetricEntity[]}
     */
    const filteredMetrics: MetricEntity[] = _.reject(relevantFamilies, (metric) => {
      const { id, variants } = metric;

      return (
        (_.includes(excludedMetrics, id) && _.size(_.without(variants, ...excludedVariants)) === 0) ||
        (isOptionDisabled || _.stubFalse)(metric)
      );
    });

    return _.map(filteredMetrics, ({ id, sourceTypes }) => {
      const label = _.join(
        _.map(sourceTypes, (sourceType) => t<string>(config.source(sourceType))),
        ", "
      );

      return {
        value: id,
        label
      };
    });
  }, [familyMembers, blacklist, isOptionDisabled, farm]);

  /**
   * Get the list of metric options
   * @type {() => {value: unknown, label: any}[]}
   */
  const getMetricListOptions = useDeepCompareCallback(() => {
    /**
     * All the excluded variants
     * @type {string[]}
     */
    const excluded = _.compact(_.map(blacklist, "variantID"));

    /**
     * Filter the variants based on the excluded ones
     * @type {string[]}
     */
    const filteredVariants = _.reject(_.without(supportedVariants, ...excluded), (variantID) => {
      const variant = dataDescriptors[variantID];

      return (isOptionDisabled || _.stubFalse)(variant);
    });

    return _.compact(
      _.map(filteredVariants, (variant) => {
        /**
         * Get the type of the variant
         * @type {DataDescriptorEntity}
         */
        const type = _.get(dataDescriptors, [variant, "dataProperties", "type"]);

        return isSandboxEnabled && type === "dimGroup"
          ? null
          : {
              value: variant,
              label: t(config.variant(type))
            };
      })
    );
  }, [supportedVariants, dataDescriptors, isSandboxEnabled]);

  const {
    flags: { isBackendAggregated }
  } = getMetadata(variantID);

  const hasSeriesFilter = _.size(getSeries(variantID).available) > 1;

  /**
   * Tell whether the chart filters should be visible
   * @type {boolean}
   */
  const hasFilters = hasSeriesFilter || isBackendAggregated;

  return (
    <>
      {isDebugMode() ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ReactJson
            collapsed
            style={{
              border: "1px dashed #ccc",
              padding: 16,
              margin: "16px 16px 0",
              maxHeight: 300,
              overflow: "scroll"
            }}
            src={{
              filePath:
                "packages/apps/enterprise/src/modules/analytics/components/chart-layout/v3/ChartSelector/ChartSelector.tsx",
              metric: metrics[metricID],
              variant: variantID ? dataDescriptors[variantID] : void 0
            }}
          />
        </div>
      ) : null}
      <Row gutter={8} className={styles.Selector} wrap={false} {...rest}>
        <Col flex={1}>
          <MetricPicker
            isOptionDisabled={isOptionDisabled}
            disabled={disabled}
            value={metricID}
            blacklist={blacklist}
            onChange={(metric) => {
              GAEvent(
                GACategories.configurator,
                "Select metric",
                JSON.stringify({
                  metric: _.get(metrics, [metric, "name"])
                })
              );

              onChange(metric, trackVariant(metric));
            }}
          />
        </Col>
        {!!_.size(familyMembers) && (
          <Col>
            <Dropdown
              size="large"
              disabled={disabled || _.size(getOrderedMetrics()) === 1}
              icon={<BrowserOutlined />}
              placeholder={t(config.placeholders.source)}
              selection={metricID}
              onChange={(metric) => {
                GAEvent(
                  GACategories.configurator,
                  "Select source",
                  JSON.stringify({
                    source: _.get(metric, [metric, "name"])
                  })
                );

                onChange(metric, trackVariant(metric));
              }}
              values={getOrderedMetrics()}
            />
          </Col>
        )}
        <Col>
          <Dropdown
            size="large"
            icon={<Icon component={ActionKey} />}
            placeholder={t(config.placeholders.variant)}
            disabled={disabled || !farm || _.size(getMetricListOptions()) === 1}
            selection={variantID}
            onChange={(variant) => {
              GAEvent(
                GACategories.configurator,
                "Select variant",
                JSON.stringify({
                  variant: t(config.variant(_.get(dataDescriptors, [variant, "dataProperties", "type"])))
                })
              );

              onChange(metricID, variant);
            }}
            values={getMetricListOptions()}
          />
        </Col>
        {hasFilters && (
          <Col>
            <Filter
              variantID={variantID}
              type={_.get(dataDescriptors, [variantID, "dataProperties", "seriesType"])}
            />
          </Col>
        )}
        {!!chart && (
          <Col>
            <Tooltip title={<Markdown>{t<string>(description(chart.entity.title))}</Markdown>}>
              <Button className={styles.Info} size="large" icon={<InfoCircleOutlined />}>
                {t(config.infoTip)}
              </Button>
            </Tooltip>
          </Col>
        )}
      </Row>
    </>
  );
};
